import { useEffect } from 'react';
import { useAuthStore } from '@/store/useAuthStore';
import { tokenService } from '@/services/token/token-service';
import ENVIRONMENT from '../../../environment/environment';

const WIDGET_SCRIPT_ID = 'toppion-ticket-widget-script';

type TicketWidgetEmbedProps = {
  enabled?: boolean;
};

/**
 * Nhúng ticket widget — chỉ chạy sau khi đã login và có profile.id.
 * Đẩy thông tin nhân viên: Id, TenNhanVien, cvID, bpID, site.
 */
export function TicketWidgetEmbed({ enabled = true }: TicketWidgetEmbedProps) {
  const hydrated = useAuthStore((s) => s.hydrated);
  const accessToken = useAuthStore((s) => s.accessToken);
  const tenantID = useAuthStore((s) => s.tenantID);
  const profile = useAuthStore((s) => s.profile);

  const userId = profile?.id;
  const tenNhanVien = profile?.tenNhanVien ?? '';
  const cvID = profile?.chucVuId ?? '';
  const bpID = profile?.boPhanId ?? '';
  const tenBoPhan = profile?.tenBoPhan ?? '';
  const tenChucVu = profile?.tenChucVu ?? '';
  const site = tenantID ?? '';

  const isLoggedIn =
    enabled
    && hydrated
    && Boolean(accessToken && !tokenService.isTokenExpired(accessToken))
    && Boolean(userId);

  useEffect(() => {
    if (!isLoggedIn || !userId) {
      document.getElementById(WIDGET_SCRIPT_ID)?.remove();
      return;
    }

    if (document.getElementById(WIDGET_SCRIPT_ID)) return;

    if (!accessToken) return;

    const baseAuthenUrl = process.env.NODE_ENV === 'production'
      ? ENVIRONMENT.API_HOST
      : String(process.env.CONFIG_NEXT_PUBLIC_API_BASE_URL || ENVIRONMENT.API_HOST_DEV);
    const baseUrl = 'https://tickets.toppion.com.vn';
    const cacheBuster = Math.random();

    const script = document.createElement('script');
    script.id = WIDGET_SCRIPT_ID;
    script.src = `${baseUrl}/widget-embed.js?t=${cacheBuster}`;
    script.async = true;
    script.setAttribute('data-widget-url', `${baseUrl}/widget-popup?t=${cacheBuster}`);
    script.setAttribute('data-user-id', userId);
    script.setAttribute('data-token', accessToken);
    script.setAttribute('data-url-auth', `${baseAuthenUrl}/v1`);
    script.setAttribute('data-button-text', 'TICKET');
    script.setAttribute('data-button-color', '#1890ff');

    // Thông tin nhân viên
    script.setAttribute('data-id', userId);
    script.setAttribute('data-ten-nhan-vien', tenNhanVien);
    script.setAttribute('data-cv-id', cvID);
    script.setAttribute('data-bp-id', bpID);
    script.setAttribute('data-ten-bo-phan', tenBoPhan);
    script.setAttribute('data-ten-chuc-vu', tenChucVu);
    script.setAttribute('data-site', site);

    document.body.appendChild(script);

    return () => {
      document.getElementById(WIDGET_SCRIPT_ID)?.remove();
    };
  }, [isLoggedIn, userId, accessToken, tenNhanVien, cvID, bpID, tenBoPhan, tenChucVu, site]);

  return null;
}
